import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_CLIENT } from '../../providers/supabase.provider';

@Injectable({
  providedIn: 'root'
})
export class SupabaseStorageService {

  private supabaseClient: SupabaseClient;

  constructor() {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  async uploadAvatar(file: File, fileName: string, oldUrl: string): Promise<{ error: any, publicUrl: string }> {
    try {
      const defaultUrl = '/public/assets/defaults/default_avatar.jpg';
      if (oldUrl && oldUrl !== defaultUrl) {
        const fileNameToDelete = oldUrl.split('/').pop();
        if (fileNameToDelete) {
          const { error: deleteError } = await this.supabaseClient.storage
            .from('avatar')
            .remove([fileNameToDelete]);
          if (deleteError) {
            return { error: deleteError, publicUrl: '' };
          }
        }
      }
      const { error } = await this.supabaseClient.storage
        .from('avatar')
        .upload(fileName, file);
      if (error) {
        return { error, publicUrl: '' };
      }
      const { publicUrl } = this.supabaseClient.storage
        .from('avatar')
        .getPublicUrl(fileName).data;
      if (!publicUrl) {
        throw new Error('未能獲取公開 URL');
      }
      return { error: null, publicUrl };
    } catch (error) {
      return { error, publicUrl: '' };
    }
  }

  async uploadTaskImage(file: File, fileName: string): Promise<{ error: any, publicUrl: string }> {
    try {
      // 使用標準 MIME 與副檔名，避免照片被 Storage 當作一般檔案。
      const contentType = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
      const extension = contentType === 'image/jpeg' ? 'jpg' : contentType.split('/')[1];
      const imagePath = /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}.${extension}`;
      // 先讀取實際內容；使用 ArrayBuffer 讓 SDK 直接傳送二進位 body，
      // 避免 File/Blob 被包成 multipart 後，Storage 無法取得檔案部分。
      let image: ArrayBuffer;
      try {
        image = await file.arrayBuffer();
      } catch {
        throw new Error('無法讀取照片內容，請重新選擇照片');
      }
      if (!image.byteLength || image.byteLength !== file.size) {
        throw new Error('照片內容為空或不完整，請重新選擇照片');
      }
      const { error } = await this.supabaseClient.storage
        .from('task_image')
        .upload(imagePath, image, { contentType });
      if (error) {
        return { error, publicUrl: '' };
      }
      const { publicUrl } = this.supabaseClient.storage
        .from('task_image')
        .getPublicUrl(imagePath).data;
      if (!publicUrl) {
        throw new Error('未能獲取公開 URL');
      }
      return { error: null, publicUrl };
    } catch (error) {
      return { error, publicUrl: '' };
    }
  }

  async uploadIdentityDocument(file: File, fileName: string): Promise<{ error: any, publicUrl: string }> {
    try {
      const { error } = await this.supabaseClient.storage
        .from('identity_document')
        .upload(fileName, file);
      if (error) {
        return { error, publicUrl: '' };
      }
      const { publicUrl } = this.supabaseClient.storage
        .from('identity_document')
        .getPublicUrl(fileName).data;
      if (!publicUrl) {
        throw new Error('未能獲取公開 URL');
      }
      return { error: null, publicUrl };
    } catch (error) {
      return { error, publicUrl: '' };
    }
  }

  async uploadApplyFile(file: File, fileName: string): Promise<{ error: any, publicUrl: string }> {
    try {
      const { error } = await this.supabaseClient.storage
        .from('apply_files')
        .upload(fileName, file);
      if (error) {
        return { error, publicUrl: '' };
      }
      const { publicUrl } = this.supabaseClient.storage
        .from('apply_files')
        .getPublicUrl(fileName).data;
      if (!publicUrl) {
        throw new Error('未能獲取公開 URL');
      }
      return { error: null, publicUrl };
    } catch (error) {
      return { error, publicUrl: '' };
    }
  }

  async uploadAnnouncementImage(file: File, fileName: string): Promise<{ error: any, publicUrl: string }> {
    try {
      const { error } = await this.supabaseClient.storage
        .from('announcement_image')
        .upload(fileName, file);
      if (error) {
        return { error, publicUrl: '' };
      }
      const { publicUrl } = this.supabaseClient.storage
        .from('announcement_image')
        .getPublicUrl(fileName).data;
      if (!publicUrl) {
        throw new Error('未能獲取公開 URL');
      }
      return { error: null, publicUrl };
    } catch (error) {
      return { error, publicUrl: '' };
    }
  }

  async uploadVoiceFile(blob: Blob, fileName: string, existingUrl?: string): Promise<{ error: any; publicUrl: string }> {
    try {
      const file = new File([blob], fileName, { type: blob.type, lastModified: Date.now() });

      const { error } = await this.supabaseClient.storage
        .from('voice_files')
        .upload(fileName, file);

      if (error) {
        return { error, publicUrl: '' };
      }

      const { data: publicData } = this.supabaseClient.storage
        .from('voice_files')
        .getPublicUrl(fileName);

      if (!publicData || !publicData.publicUrl) {
        throw new Error('未能獲取公開 URL');
      }

      const publicUrl = publicData.publicUrl;

      // 新錄音確認上傳成功後才刪除舊檔，避免上傳失敗時遺失原錄音。
      if (existingUrl) {
        await this.deleteOldFile(existingUrl);
      }

      return { error: null, publicUrl };
    } catch (error) {
      return { error, publicUrl: '' };
    }
  }

  async deleteOldFile(existingUrl?: string): Promise<void> {
    if (!existingUrl) {
      console.error('未提供有效的 URL');
      return;
    }

    try {
      const fileName = existingUrl.split('/').pop();
      if (!fileName) {
        throw new Error('無法從 URL 取得檔案名稱');
      }

      const { error } = await this.supabaseClient.storage
        .from('voice_files')
        .remove([fileName]);

      if (error) {
        console.error('Error deleting old file:', error.message);
      } else {
        console.log(`Old file ${fileName} deleted successfully.`);
      }
    } catch (error) {
      console.error('Error in deleteOldFile:', error);
    }
  }

  async deleteAvatarByUrl(url: string): Promise<{ error: any }> {
    const defaultUrl = '/public/assets/defaults/default_avatar.jpg';
    if (url === defaultUrl) {
      return { error: null };
    } else {
      const fileName = url.split('/').pop();
      if (!fileName) {
        return { error: new Error('未能獲取文件名') };
      }
      const { error } = await this.supabaseClient.storage
        .from('avatar')
        .remove([fileName]);
      return { error };
    }
  }

  async deleteTaskImageByUrl(url: string): Promise<{ error: any }> {
    if (url != '/public/assets/defaults/default_task_image.png') {
      try {
        const fileName = url.split('/').pop();
        if (!fileName) {
          throw new Error('未能獲取文件名');
        }
        const { error } = await this.supabaseClient.storage
          .from('task_image')
          .remove([fileName]);
        if (error) {
          return { error };
        }
        return { error: null };
      } catch (error) {
        return { error };
      }
    }
    return { error: null };
  }

  async deleteApplyFileByUrl(url: string): Promise<{ error: any }> {
    try {
      const fileName = url.split('/').pop();
      if (!fileName) {
        throw new Error('未能獲取文件名');
      }
      const { error } = await this.supabaseClient.storage
        .from('apply_files')
        .remove([fileName]);
      if (error) {
        return { error };
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  // 身分證件 bucket 為私有，舊資料存的是公開網址，新資料則可能含使用者資料夾
  private getIdentityDocumentPath(url: string): string | null {
    const marker = '/identity_document/';
    const markerIndex = url.indexOf(marker);
    const path = markerIndex >= 0 ? url.slice(markerIndex + marker.length) : url;
    return path ? decodeURIComponent(path.split('?')[0]) : null;
  }

  async createIdentityDocumentSignedUrl(url: string, expiresInSeconds = 300): Promise<string> {
    const path = this.getIdentityDocumentPath(url);
    if (!path) {
      throw new Error('未能獲取文件路徑');
    }

    const { data, error } = await this.supabaseClient.storage
      .from('identity_document')
      .createSignedUrl(path, expiresInSeconds);
    if (error) {
      throw error;
    }

    return data.signedUrl;
  }

  async deleteIdentityDocumentByUrl(url: string): Promise<{ error: any }> {
    try {
      const fileName = this.getIdentityDocumentPath(url);
      if (!fileName) {
        throw new Error('未能獲取文件名');
      }
      const { error } = await this.supabaseClient.storage
        .from('identity_document')
        .remove([fileName]);
      if (error) {
        return { error };
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  async deleteAnnouncementImageByUrl(url: string): Promise<{ error: any }> {
    try {
      const fileName = url.split('/').pop();
      if (!fileName) {
        throw new Error('未能獲取文件名');
      }
      const { error } = await this.supabaseClient.storage
        .from('announcement_image')
        .remove([fileName]);
      if (error) {
        return { error };
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  async deleteVoiceFileByUrl(url: string): Promise<{ error: any }> {
    try {
      const fileName = url.split('/').pop();
      if (!fileName) {
        throw new Error('未能獲取文件名');
      }
      const { error } = await this.supabaseClient.storage
        .from('voice_files')
        .remove([fileName]);
      if (error) {
        return { error };
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  }

}
